import { useRef, useState } from "react";

interface ImageUploaderProps {
  value: string;
  onChange: (url: string) => void;
  accentColor?: string;
  folder?: "products" | "promotions" | "blog";
}

export default function ImageUploader({
  value,
  onChange,
  accentColor = "#00b4c8",
  folder = "products",
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file: File) => {
    setError("");

    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

    if (!allowed.includes(file.type)) {
      setError("Solo se permiten imágenes PNG, JPG o WEBP.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("La imagen no debe superar 5 MB.");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("image", file);
      formData.append("folder", folder);

      const res = await fetch("/api/upload.php", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Error al subir la imagen.");
      }

      onChange(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al subir la imagen.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setError("");
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {value ? (
        <div className="relative w-full h-36 rounded-xl overflow-hidden border border-[#c9b8f0]/40 group">
          <img
            src={value}
            alt="preview"
            className="w-full h-full object-cover object-top"
          />

          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="px-3 py-1.5 bg-white rounded-lg text-xs font-medium text-[#2d1a0e] cursor-pointer hover:bg-gray-100 transition-colors disabled:opacity-60"
            >
              {uploading ? "Subiendo..." : "Cambiar"}
            </button>

            <button
              type="button"
              onClick={handleRemove}
              className="px-3 py-1.5 bg-red-500 rounded-lg text-xs font-medium text-white cursor-pointer hover:bg-red-600 transition-colors"
            >
              Quitar
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          className={`w-full h-36 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${
            dragging
              ? "border-[#00b4c8] bg-[#00b4c8]/5"
              : "border-[#c9b8f0]/50 hover:border-[#c9b8f0] bg-[#fdf6f0]/50 hover:bg-[#fdf6f0]"
          }`}
          style={dragging ? { borderColor: accentColor } : {}}
        >
          <span
            className="w-10 h-10 flex items-center justify-center rounded-full"
            style={{ backgroundColor: `${accentColor}15` }}
          >
            <i className="ri-image-add-line text-xl" style={{ color: accentColor }}></i>
          </span>

          <div className="text-center">
            <p className="text-sm font-medium text-[#2d1a0e]">
              {uploading ? "Subiendo imagen..." : "Subir imagen"}
            </p>
            <p className="text-xs text-[#8a6a55] mt-0.5">
              PNG, JPG o WEBP · máx. 5 MB
            </p>
            <p className="text-xs text-[#b09080] mt-0.5">
              Haz clic o arrastra aquí
            </p>
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line text-xs"></i>
          {error}
        </p>
      )}
    </div>
  );
}
